/**
 * DoseSpot connect card for the Organization detail page in admin-v2.
 *
 * Staff-triggered clinic setup on behalf of an organization (GDN-1014):
 * connects the org to DoseSpot without requiring anyone in the org to hold a
 * JotRx entitlement. Mirrors the provider-facing EPrescriptionOrgSetupModal
 * form fields against the admin setup-clinic endpoint.
 */

import { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import { MedicalServices } from '@mui/icons-material';
import { US_STATES } from '@shared/constants/states';
import { useSnackbar } from 'notistack';

import { useAdminSetupDosespotClinicMutation } from '../store/api';

/** DoseSpot API field length limits (v1.7.1, Dec 2025) */
const DOSESPOT_MAX = { name: 35, address: 35, city: 35 } as const;

/** Strip to digits and drop a leading US country code (matches EPrescriptionOrgSetupModal). */
const extractDigits = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  return digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;
};

interface OrganizationDosespotConnectCardProps {
  organizationId: string;
  /** Suggested clinic name (pre-filled when it fits DoseSpot's limit). */
  organizationName?: string | null;
  /** Called after a successful connect so the parent can refresh org state. */
  onConnected?: () => void;
}

interface FormState {
  clinicName: string;
  phone: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
}

export function OrganizationDosespotConnectCard({
  organizationId,
  organizationName,
  onConnected,
}: OrganizationDosespotConnectCardProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [setupClinic, { isLoading: isSubmitting }] = useAdminSetupDosespotClinicMutation();

  const [form, setForm] = useState<FormState>({
    clinicName:
      organizationName && organizationName.length <= DOSESPOT_MAX.name ? organizationName : '',
    phone: '',
    addressLine1: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null);
  };

  const validate = (): string | null => {
    if (!form.clinicName.trim()) return 'Clinic name is required';
    if (form.clinicName.trim().length > DOSESPOT_MAX.name)
      return `Clinic name must be ${DOSESPOT_MAX.name} characters or fewer (DoseSpot limit)`;
    if (extractDigits(form.phone).length !== 10)
      return 'Please enter a valid 10-digit phone number';
    if (!form.addressLine1.trim()) return 'Address is required';
    if (!form.city.trim()) return 'City is required';
    if (!form.state.trim()) return 'State is required';
    if (!(form.state.trim().toUpperCase() in US_STATES))
      return 'Please use a two-letter state abbreviation (e.g., MA, CA, NY)';
    if (!/^\d{5}(-\d{4})?$/.test(form.zipCode.trim()))
      return 'ZIP code must be 5 digits (12345) or 5+4 format (12345-6789)';
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await setupClinic({
        organizationId,
        adminSetupClinicRequest: {
          clinic: {
            clinic_name: form.clinicName.trim(),
            phone: extractDigits(form.phone),
            address_line1: form.addressLine1.trim(),
            city: form.city.trim(),
            state: form.state.trim().toUpperCase(),
            zip_code: form.zipCode.trim(),
          },
        },
      }).unwrap();

      enqueueSnackbar('Organization connected to DoseSpot', { variant: 'success' });
      onConnected?.();
    } catch (err: unknown) {
      let message = 'Failed to connect the organization to DoseSpot. Please try again.';
      if (err && typeof err === 'object' && 'data' in err) {
        const data = (err as { data?: { message?: string } }).data;
        if (data?.message) message = data.message;
      }
      setError(message);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <MedicalServices sx={{ fontSize: 20, color: 'primary.main' }} />
        <Typography variant="h6">Connect to DoseSpot</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create the DoseSpot clinic for this organization on its behalf. This bypasses the
        JotRx-entitlement gate in the practice-facing setup flow — use it when no practice admin can
        run the connect flow themselves.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Clinic Name"
            value={form.clinicName}
            onChange={handleChange('clinicName')}
            disabled={isSubmitting}
            required
            slotProps={{ htmlInput: { maxLength: DOSESPOT_MAX.name } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Phone Number"
            value={form.phone}
            onChange={handleChange('phone')}
            disabled={isSubmitting}
            required
            placeholder="617-555-1234"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Street Address"
            value={form.addressLine1}
            onChange={handleChange('addressLine1')}
            disabled={isSubmitting}
            required
            slotProps={{ htmlInput: { maxLength: DOSESPOT_MAX.address } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="City"
            value={form.city}
            onChange={handleChange('city')}
            disabled={isSubmitting}
            required
            slotProps={{ htmlInput: { maxLength: DOSESPOT_MAX.city } }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField
            fullWidth
            size="small"
            label="State"
            value={form.state}
            onChange={handleChange('state')}
            disabled={isSubmitting}
            required
            placeholder="MA"
            helperText="Two-letter abbreviation"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField
            fullWidth
            size="small"
            label="ZIP Code"
            value={form.zipCode}
            onChange={handleChange('zipCode')}
            disabled={isSubmitting}
            required
            placeholder="02101"
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {isSubmitting ? 'Connecting…' : 'Connect Clinic'}
        </Button>
      </Box>
    </div>
  );
}
